import type { ReactNode } from 'react';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';
import { cn } from '../../../../shared';
import type { RoleBoard } from '../../types/taActionBoard';

export interface StatusChip {
  label: string;
  bg: string;
  text: string;
}

interface RoleAccordionProps {
  /**
   * Only these four fields are read, so a tab that groups something other than a full RoleBoard
   * (Candidate requests groups inbound mail by role) can reuse this header without having to
   * invent the rest of a RoleBoard.
   */
  role: Pick<RoleBoard, 'postingId' | 'roleTitle' | 'hiringManagerName' | 'candidateCount'>;
  /** Right-aligned status chip; omit for empty roles (they show "nothing open") */
  chip?: StatusChip | null;
  /** Whether this role has open items in the current tab */
  hasOpenItems: boolean;
  /** Overrides the default "{hiring manager} · N candidates" sub-line. */
  subtitle?: ReactNode;
  /**
   * Rendered left of the status chip, whether or not the role has open items — the interview
   * availability light belongs to the role, not to its queue.
   */
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * One role accordion: header = role title, hiring manager, "N candidates",
 * right status chip. Empty roles render VERY light (gray-300/-400 text, washed
 * green chip) so the rows with open work visually dominate every tab.
 */
export function RoleAccordion({ role, chip, hasOpenItems, subtitle, aside, children }: RoleAccordionProps) {
  return (
    <AccordionItem
      value={role.postingId}
      className={cn(
        'rounded-xl border bg-white px-4',
        hasOpenItems ? 'border-gray-200' : 'border-gray-100'
      )}
    >
      <AccordionTrigger heading="h3" className="py-3 hover:no-underline">
        <div className="flex w-full items-center justify-between gap-3 pr-2">
          <div className="min-w-0 text-left">
            <div
              className={cn(
                'truncate text-sm font-semibold',
                hasOpenItems ? 'text-gray-900' : 'text-gray-400'
              )}
            >
              {role.roleTitle}
            </div>
            {subtitle && (
              <div
                className={cn(
                  'truncate text-xs font-normal',
                  hasOpenItems ? 'text-gray-500' : 'text-gray-300'
                )}
              >
                {subtitle}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
          {aside}
          {hasOpenItems && chip ? (
            <span
              className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{ backgroundColor: chip.bg, color: chip.text }}
            >
              {chip.label}
            </span>
          ) : (
            <span className="shrink-0 rounded-full bg-green-50/60 px-2.5 py-1 text-xs font-medium text-green-600/70">
              nothing open
            </span>
          )}
          </div>
        </div>
      </AccordionTrigger>
      {/*
        wrapperClassName must NOT carry padding: the DS AccordionContent is
        force-mounted and collapses via height 0 + overflow-hidden — padding on
        the wrapper keeps a padding-high strip of the body visible when closed
        (the "half-cut chips" bleed). Bottom padding lives on the inner div
        (className), which is fully clipped by the h-0 wrapper.
      */}
      <AccordionContent className="pb-4 pt-1">{children}</AccordionContent>
    </AccordionItem>
  );
}

/** Summary chip used in the strip above each tab's role list. */
/**
 * A clickable filter chip (mockup `.fchip`): a white pill with an optional
 * status dot, its label, and a muted count — turning trustedBlue/lightBlue when
 * it is the active filter. Clicking the active one clears the filter, so the
 * chip that filters can always unfilter. `aria-pressed` exposes the toggle state.
 */
export function FilterChip({
  label,
  count,
  dot,
  active,
  onClick,
}: {
  label: string;
  count: number;
  /** Status-dot colour before the label; omit for the neutral "All" chip. */
  dot?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-solid px-3 py-1.5 text-[13px] font-medium transition',
        active
          ? 'border-[#0072EA] bg-[#DBEBFC] text-[#0072EA]'
          : 'border-[#E9EAEC] bg-white text-[#1A1A1A] hover:bg-[#F8F8F8]'
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: dot }} />}
      {label}
      <span className={active ? 'text-xs text-[#0072EA]' : 'text-xs text-[#707070]'}>{count}</span>
    </button>
  );
}

export function SummaryChip({ label, bg, text }: StatusChip) {
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold"
      style={{ backgroundColor: bg, color: text }}
    >
      {label}
    </span>
  );
}
